import * as S from "../ConvenienceTest.style";
import { useContext, useEffect, useState } from "react";
import Auth from "../../../Auth/Auth";
import { UserIdentityType } from "../../../services/UserIdentitiesService";
import SavingsPlanService, {
  SavingsPlanType,
  SavingsPlanTypes,
} from "../../../services/SavingsPlanService";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { SignatureComponent } from "../../../components/Signature/SignatureComponent";
import Moment from "moment";
import { ISignature } from "../../../services/SignaturesService";
import { Spinner, SpinnerSize } from "@fluentui/react";
import scrollToTop from "../../../utils/scrollToTop";
import React from "react";
import DocumentTitle from "react-document-title";
import { useDispatch } from "react-redux";
import { setActiveSavingsPlan } from "../../../store/auth/actions";
import { ThemeContext } from "@fluentui/react-theme-provider";
import { TicketSupportContainer } from "../../TicketSupport/TicketSupport.style";
import TicketSupport from "../../TicketSupport/TicketSupport";
import { ScreenStepType } from "../../TicketSupport/TicketSupportConstants";
import useTicketSupport from "../../../hooks/useTicketSupport";
import { useContractStepGuard } from "@/screen/ContractManagement/hooks/useContractStepGuard";
import {
  ContractSignatureAdvanceStepKeys,
  ContractSignatureStepKeys,
} from "@/screen/ContractManagement/contractSignatureStepKeys";
import {
  ContractOnboardingStrings,
  resolveContractOnboardingError,
} from "@/screen/Map/contractOnboarding.strings";
import { buildMapNavigationState } from "@/screen/Map/resolveContractOnboardingStepNavigation";
import ModalComplete from "@/components/Modals/ModalComplete";

const ConvenienceTestConfirm = () => {
  const { savingsPlanId } = useParams<any>();
  const history = useHistory();
  const guard = useContractStepGuard(
    Number(savingsPlanId),
    ContractSignatureStepKeys,
  );
  const isPromotioned = Auth.isPromotioned();
  const theme = useContext(ThemeContext);
  const [userIdentities, setUserIdentities] = useState<UserIdentityType[]>([]);
  const [savingsPlan, setSavingsPlan] = useState<SavingsPlanType>();
  const [phoneNumbers, setPhoneNumbers] = useState("");
  const [modalShow, setModalShow] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState();
  const [advanceError, setAdvanceError] = useState<string | null>(null);
  const { state } = useLocation<any>();
  const dispatch = useDispatch();

  const ticketSupport = useTicketSupport({
    component: "ConvenienceTestConfirm",
    screenName: ScreenStepType.FirmaContratoPlanAhorroConvenience,
    savingPlanId: savingsPlanId,
  });

  const processExpirationDate = (signatures: ISignature[]) => {
    if (signatures !== undefined && signatures.length > 0) {
      signatures.forEach((signature: ISignature) => {
        if (signature && typeof signature.expiresAt === "string") {
          signature.expiresAt = Moment(signature.expiresAt).toDate();
        }
      });
    }
  };

  let defaultSignatures: any = state?.signatures;
  processExpirationDate(defaultSignatures);

  const [signatures, setSignatures] = useState(defaultSignatures);

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    SavingsPlanService.getSavingsPlan(savingsPlanId).then(
      (savingsPlan: SavingsPlanType) => {
        setSavingsPlan(savingsPlan);
        console.log("SAVING PLAN:", savingsPlan);
      },
      (error) => {
        setError(error);
        setIsLoaded(true);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      savingsPlan !== undefined &&
      savingsPlan.savingsPlansUserIdentities !== undefined
    ) {
      setUserIdentities(
        savingsPlan.savingsPlansUserIdentities.map(
          (savingsPlanUserIdentity) => savingsPlanUserIdentity.userIdentity,
        ),
      );
      setIsLoaded(true);
    }
  }, [savingsPlan]);

  useEffect(() => {
    setPhoneNumbers(
      userIdentities
        .filter((uI) => {
          var currentDate = Moment();
          var birthdayDate = Moment(uI.birthday);
          return currentDate.diff(birthdayDate, "years") > 17;
        })
        .map(
          (userIdentity: any) =>
            "X".repeat(userIdentity.phone.length - 3) +
            userIdentity.phone.slice(
              userIdentity.phone.length - 3,
              userIdentity.phone.length,
            ),
        )
        .join(", "),
    );
  }, [userIdentities]);

  // Null en la sección 4: el plan ya está contratado y esta pantalla sólo re-firma una versión
  // nueva del contrato, así que no hay paso de configuración que cerrar ni status que tocar.
  const advanceStepKey =
    guard.activeStepKey !== null &&
    ContractSignatureAdvanceStepKeys.includes(guard.activeStepKey)
      ? guard.activeStepKey
      : null;

  // El paso lo cierra el orquestador, no un PATCH de configurationMapStep: sólo así se valida que
  // el contrato esté realmente firmado antes de mandar el plan a verificación.
  const advanceAfterSignature = async (stepKey: string): Promise<boolean> => {
    const result = await SavingsPlanService.advancePlanStep(
      Number(savingsPlanId),
      stepKey,
    );
    if (!result.success) {
      setAdvanceError(
        resolveContractOnboardingError(
          result.errors?.[0],
          ContractOnboardingStrings.continueContractFallbackError,
        ),
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    // El modal de alta completada se abre antes de las llamadas, igual que antes de migrar el
    // avance: el push posterior es quien decide si llega a verse.
    if (!state?.signatures || !modalShow) {
      scrollToTop();
      if (advanceStepKey !== null) {
        setModalShow(true);
      }
    }

    if (savingsPlan === undefined) {
      return;
    }

    if (advanceStepKey === null) {
      const activePlan = Auth.getActivePlan();
      Auth.setActivePlan({ ...activePlan, outdatedContract: false });
      await dispatch(
        setActiveSavingsPlan({
          ...activePlan,
          outdatedContract: false,
          sync: true,
        }),
      );
      history.push(
        "/configurationMap",
        buildMapNavigationState(Number(savingsPlanId), savingsPlan.name),
      );
      return;
    }

    setAdvanceError(null);

    // El status debe quedar en PENDING antes del avance: es lo que cierra 2.3 para
    // IsContractSignatureComplete.
    try {
      await SavingsPlanService.patchSavingsPlan(savingsPlanId, { status: 1 });
    } catch (patchError: any) {
      setError(patchError);
      return;
    }

    if (!(await advanceAfterSignature(advanceStepKey))) {
      setModalShow(false);
      return;
    }

    if (savingsPlan.type === SavingsPlanTypes.ExperienceSample) {
      history.push(`/elements-contract/${savingsPlan.id}`);
    } else {
      history.push(
        "/configurationMap",
        buildMapNavigationState(Number(savingsPlanId), savingsPlan.name),
      );
    }
  };

  if (guard.isChecking) {
    return (
      <DocumentTitle title={`IronIA - Confirmación`}>
        <React.Fragment>
          <S.HelperStyle />
          <S.Cont theme={theme}>
            <S.MainCont>
              <Spinner size={SpinnerSize.large} />
            </S.MainCont>
          </S.Cont>
        </React.Fragment>
      </DocumentTitle>
    );
  }

  return (
    <>
      {/* Soporte */}
      <TicketSupportContainer>
        <TicketSupport {...ticketSupport} />
      </TicketSupportContainer>
      <DocumentTitle title={`IronIA - Confirmación`}>
        <React.Fragment>
          <S.HelperStyle />
          <S.Cont theme={theme}>
            <S.MainCont>
              {!isLoaded && <Spinner size={SpinnerSize.large} />}
              {isLoaded && (
                <SignatureComponent
                  signatures={signatures}
                  userIdentities={userIdentities}
                  title="Firma del contrato"
                  submitButtonText="Firmar contrato"
                  cancelButtonText="Volver"
                  alreadySignedText="Enhorabuena, ¡ya tienes tu plan de cartera!"
                  headerTextArea={
                    "A continuación te incluimos los detalles del contrato con nosotros. Léelo y cuando estés listo, dale al botón " +
                    "de 'Solicitar SMS'. Te enviaremos un código SMS por cada interviniente" +
                    (userIdentities.length > 1
                      ? ` a los números ${phoneNumbers} `
                      : userIdentities.length === 1
                        ? ` al número ${phoneNumbers} `
                        : " ") +
                    "para realizar la firma del contrato."
                  }
                  backButtonText="Volver"
                  onBackButtonClicked={() =>
                    history.push("/contract/" + savingsPlanId)
                  }
                  onCancelButtonClicked={() =>
                    history.push("/contract/" + savingsPlanId)
                  }
                  requestNewCodeButtonText="Recibir nuevo código"
                  onNewCodeSended={(signatures: any) => {
                    processExpirationDate(signatures);
                    setSignatures(signatures);
                  }}
                  onSignatureChanged={(signatures: any) => {
                    processExpirationDate(signatures);
                    setSignatures(signatures);
                  }}
                  onSigned={handleSubmit}
                />
              )}
              {error && <div>Alguno de los códigos enviados no es válido</div>}
              {advanceError && (
                <div
                  style={{
                    color: "#a4262c",
                    marginTop: "0.75rem",
                    cursor: "default",
                  }}
                >
                  {advanceError}
                </div>
              )}
            </S.MainCont>
          </S.Cont>
          {modalShow && (
            <ModalComplete
              title="¡Proceso de alta completado!"
              body={[
                "Enhorabuena, has completado el proceso de verificación correctamente",
                "Un miembro del equipo de Diaphanum Valores S.V. revisará la documentación presentada y te habilitará la cuenta lo antes posible.",
                "Te mantendremos informado por correo electrónico en todo momento.",
              ]}
              buttonText="Continuar con el test"
              returnPath={"/savingsPlans"}
              setModalShow={setModalShow}
              isPromotioned={isPromotioned}
            />
          )}
        </React.Fragment>
      </DocumentTitle>
    </>
  );
};

export default ConvenienceTestConfirm;
